import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Server, 
  Users, 
  Wifi, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Loader from '../components/Loader';
import StatusCard from '../components/StatusCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityColors = {
  minor: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  major: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  critical: 'text-red-400 bg-red-500/10 border-red-500/30'
};

const statusIcons = {
  ongoing: AlertTriangle,
  investigating: AlertCircle,
  resolved: CheckCircle
};

const Status = () => {
  const [serverStatus, setServerStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchData = async () => {
    try {
      const [statusRes, historyRes, incidentsRes] = await Promise.all([
        axios.get(`${API}/server-status`),
        axios.get(`${API}/status-history?limit=24`),
        axios.get(`${API}/incidents?limit=5`)
      ]);
      
      setServerStatus(statusRes.data);
      setStatusHistory(historyRes.data.reverse());
      setIncidents(incidentsRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 1000 * 60); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Format history for chart
  const chartData = statusHistory.map((entry, index) => ({
    time: new Date(entry.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    players: entry.players,
    ping: entry.ping,
    online: entry.online ? 1 : 0
  }));

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Activity className="w-8 h-8 text-violet-400" />
                <h1 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-wide">
                  <span className="gradient-text">Statut du serveur</span>
                </h1>
              </div>
              <p className="text-gray-400">
                Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
              </p>
            </motion.div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all disabled:opacity-50"
              data-testid="refresh-status-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </section>

      {/* Main Status */}
      <section className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-6xl mx-auto">
          {/* Big Status Banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`glass rounded-xl p-8 mb-6 text-center relative overflow-hidden ${
              serverStatus?.online 
                ? 'border-green-500/30' 
                : 'border-red-500/30'
            }`}
          >
            {/* Background Glow */}
            <div className={`absolute inset-0 ${
              serverStatus?.online 
                ? 'bg-gradient-to-r from-green-500/5 to-emerald-500/5' 
                : 'bg-gradient-to-r from-red-500/5 to-rose-500/5'
            }`} />
            
            <div className="relative">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
                serverStatus?.online 
                  ? 'bg-green-500/20 pulse-online' 
                  : 'bg-red-500/20'
              }`}>
                {serverStatus?.online ? (
                  <CheckCircle className="w-10 h-10 text-green-400" />
                ) : (
                  <XCircle className="w-10 h-10 text-red-400" />
                )}
              </div>
              
              <h2 className={`font-heading font-bold text-4xl uppercase tracking-wider mb-2 ${
                serverStatus?.online ? 'text-green-400' : 'text-red-400'
              }`}>
                {serverStatus?.online ? 'SERVEUR EN LIGNE' : 'SERVEUR HORS LIGNE'}
              </h2>
              
              {serverStatus?.hostname && (
                <p className="text-gray-400">{serverStatus.hostname}</p>
              )}
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatusCard
              title="Joueurs"
              value={`${serverStatus?.players || 0}/${serverStatus?.max_players || 100}`}
              icon={Users}
              status={serverStatus?.online ? 'online' : 'offline'}
            />
            <StatusCard
              title="Ping"
              value={`${serverStatus?.ping || 0}ms`}
              icon={Wifi}
              status={serverStatus?.ping > 100 ? 'warning' : 'default'}
            />
            <StatusCard
              title="Uptime"
              value={`${serverStatus?.uptime_percentage || 100}%`}
              icon={Clock}
              status="default"
            />
            <StatusCard
              title="Statut"
              value={serverStatus?.online ? 'Opérationnel' : 'Indisponible'}
              icon={Server}
              status={serverStatus?.online ? 'online' : 'offline'}
            />
          </div>
        </div>
      </section>

      {/* Charts */}
      {chartData.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 mb-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Players Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl p-6"
              >
                <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-400" />
                  Joueurs (24h)
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="playerGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="time" 
                        stroke="#4b5563" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        stroke="#4b5563" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0a0a0a', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="players" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        fill="url(#playerGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Ping Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass rounded-xl p-6"
              >
                <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-blue-400" />
                  Latence (24h)
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="pingGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="time" 
                        stroke="#4b5563" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        stroke="#4b5563" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        unit="ms"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0a0a0a', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                        formatter={(value) => [`${value}ms`, 'Ping']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="ping" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fill="url(#pingGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Incidents */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-xl p-6"
          >
            <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-6 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Historique des incidents
            </h3>
            
            {incidents.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-400/50 mx-auto mb-3" />
                <p className="text-gray-400">Aucun incident récent à signaler</p>
              </div>
            ) : (
              <div className="space-y-4">
                {incidents.map((incident, index) => {
                  const StatusIcon = statusIcons[incident.status] || AlertCircle;
                  
                  return (
                    <motion.div
                      key={incident.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-4 rounded-lg border ${severityColors[incident.severity]}`}
                      data-testid={`incident-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <StatusIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-heading font-bold text-white uppercase tracking-wider">
                              {incident.title}
                            </h4>
                            <span className={`px-2 py-0.5 rounded text-xs uppercase tracking-wider ${
                              incident.status === 'resolved' 
                                ? 'bg-green-500/20 text-green-400'
                                : incident.status === 'ongoing'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {incident.status === 'resolved' ? 'Résolu' : 
                               incident.status === 'ongoing' ? 'En cours' : 'Investigation'}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm mb-2">{incident.description}</p>
                          <p className="text-gray-500 text-xs">
                            {new Date(incident.created_at).toLocaleString('fr-FR')}
                            {incident.resolved_at && (
                              <> • Résolu le {new Date(incident.resolved_at).toLocaleString('fr-FR')}</>
                            )}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Status;
